import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false)
  }, [location])

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const isActive = (path) => location.pathname === path

  const navLinks = [
    { label: 'Forside', path: '/' },
    { label: 'Biler', path: '/biler-til-salg' },
    { label: 'Kontakt', path: '/kontakt' },
    { label: 'Biler til salg', path: '/biler-til-salg' },
  ]

  return (
    <>
      <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || isOpen || location.pathname !== '/'
          ? 'bg-neutral-950/95 backdrop-blur-md border-b border-neutral-900/80 shadow-md py-3 sm:py-4'
          : 'bg-transparent py-4 sm:py-6'
      }`}
    >
      <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8 xl:px-12">
        {/* Desktop Layout: Centered Logo with Split Navigation */}
        <div className="hidden lg:grid grid-cols-3 items-center w-full">
          
          {/* Left Navigation Links */}
          <nav className="flex items-center gap-8 justify-start">
            {navLinks.slice(0, 2).map((link) => (
              <Link
                key={link.label + link.path}
                to={link.path}
                className={`font-sans font-medium uppercase tracking-widest text-xs transition-all duration-300 pb-1 border-b-2 ${
                  isActive(link.path)
                    ? 'text-white border-[#DFB587]'
                    : 'text-neutral-300 border-transparent hover:text-[#DFB587] hover:border-[#DFB587]/40'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Center: Logo */}
          <div className="flex justify-center">
            <Link to="/" className="flex items-center">
              <img
                src="https://assets.auxodev.dk/assets/fc45746b-58f8-47db-83c4-feb0973bc76d/recars-logo.png"
                alt="RECARS Logo"
                className="h-16 sm:h-20 lg:h-[110px] w-auto object-contain transition-transform duration-300 hover:scale-105"
              />
            </Link>
          </div>

          {/* Right Navigation Links */}
          <nav className="flex items-center gap-8 justify-end">
            {navLinks.slice(2).map((link) => {
              const isButton = link.label === 'Biler til salg';
              return (
                <Link
                  key={link.label + link.path}
                  to={link.path}
                  className={isButton
                    ? "px-5 py-2.5 border border-[#DFB587] bg-[#DFB587]/10 hover:bg-[#DFB587] hover:text-neutral-950 text-xs font-bold uppercase tracking-widest text-[#DFB587] transition-all duration-300"
                    : `font-sans font-medium uppercase tracking-widest text-xs transition-all duration-300 pb-1 border-b-2 ${
                        isActive(link.path)
                          ? 'text-white border-[#DFB587]'
                          : 'text-neutral-300 border-transparent hover:text-[#DFB587] hover:border-[#DFB587]/40'
                      }`
                  }
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

        </div>

        {/* Mobile Layout (< 1024px) */}
        <div className="flex lg:hidden items-center justify-between w-full">
          
          {/* Empty spacer to balance hamburger */}
          <div className="w-10" />

          {/* Centered Logo on Mobile */}
          <div className="flex-grow flex justify-center">
            <Link to="/" className="flex items-center">
              <img
                src="https://assets.auxodev.dk/assets/fc45746b-58f8-47db-83c4-feb0973bc76d/recars-logo.png"
                alt="RECARS Logo"
                className="h-10 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Mobile Hamburger Menu Button */}
          <div className="flex">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="w-10 h-10 flex items-center justify-center border border-white/10 hover:border-[#DFB587] text-white hover:text-[#DFB587] transition-colors duration-300"
              aria-label="Toggle menu"
            >
              <i className={`fa-solid ${isOpen ? 'fa-xmark' : 'fa-bars'} text-lg`} />
            </button>
          </div>

        </div>
      </div>

      {/* Mobile Navigation Drawer */}
      {isOpen && (
        <div className="lg:hidden fixed inset-0 z-[9999] flex h-[100dvh] w-screen flex-col bg-neutral-950 overscroll-contain">
          {/* Mobile Drawer Header */}
          <div className="flex shrink-0 items-center justify-between border-b border-neutral-900 px-6 py-6">
            <div className="w-10" />
            <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center">
              <img
                src="https://assets.auxodev.dk/assets/fc45746b-58f8-47db-83c4-feb0973bc76d/recars-logo.png"
                alt="RECARS Logo"
                className="h-10 w-auto object-contain"
              />
            </Link>
            <button
              onClick={() => setIsOpen(false)}
              className="flex h-10 w-10 items-center justify-center border border-white/10 text-white transition-colors duration-300 hover:border-[#DFB587] hover:text-[#DFB587]"
              aria-label="Close menu"
            >
              <i className="fa-solid fa-xmark text-lg" />
            </button>
          </div>

          <nav className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-8 pb-10">
            {navLinks.map((link) => (
              <Link
                key={link.label + link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`font-display text-xl font-bold uppercase tracking-wider py-2 border-b border-neutral-900 transition-colors ${
                  isActive(link.path)
                    ? 'text-[#DFB587]'
                    : 'text-neutral-300 hover:text-[#DFB587]'
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Contact Info on Mobile */}
            <div className="flex flex-col gap-4 mt-6 pt-6 border-t border-neutral-900">
              <span className="text-xs font-bold uppercase tracking-widest text-neutral-500">Kontakt os:</span>
              <a
                href="tel:+4571999177"
                className="flex items-center gap-3 text-lg font-bold text-white transition-colors hover:text-[#DFB587]"
              >
                <i className="fa-solid fa-phone text-[#DFB587]" />
                <span>+45 71 99 91 77</span>
              </a>
              <a
                href="mailto:info@recars.dk"
                className="flex items-center gap-3 text-neutral-300 transition-colors hover:text-[#DFB587]"
              >
                <i className="fa-solid fa-envelope text-[#DFB587]" />
                <span>info@recars.dk</span>
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>

    {/* Floating Contact Button in Right Bottom Corner */}
    {!location.pathname.startsWith('/biler-til-salg/') || location.pathname === '/biler-til-salg' ? (
      <div className="fixed bottom-6 right-6 z-50">
        <a
          href="tel:+4571999177"
          className="flex items-center gap-3 px-5 py-3.5 border-2 border-[#DFB587] bg-neutral-950/90 backdrop-blur-md hover:bg-[#DFB587] hover:text-neutral-950 text-xs font-bold uppercase tracking-widest text-[#DFB587] transition-all duration-300 shadow-[0_0_20px_rgba(223,181,135,0.25)] hover:shadow-[0_0_30px_rgba(223,181,135,0.5)] group"
        >
          <i className="fa-solid fa-phone text-sm animate-pulse group-hover:scale-110 transition-transform duration-300" />
          <span>+45 71 99 91 77</span>
        </a>
      </div>
    ) : null}
  </>
)
}
